import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useScope } from "@/hooks/use-scope";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  listShipmentIssuePolicies,
  updateShipmentIssuePolicy,
  type ShipmentIssuePolicyRow,
} from "@/lib/shipment-issue-policies.functions";
import { SHIPMENT_ISSUE_COPY } from "@/lib/shipment-issue-copy";
import type { ShipmentExceptionReasonCode } from "@/lib/shipment-exception-signals.server";

export const Route = createFileRoute("/_authenticated/app/settings/shipment-issues")({
  head: () => ({
    meta: [
      { title: "Shipment issues — Settings" },
      { name: "description", content: "Control which carrier-reported shipment issues appear on the customer portal." },
    ],
  }),
  component: ShipmentIssuePoliciesPage,
});

function ShipmentIssuePoliciesPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listShipmentIssuePolicies);
  const updateFn = useServerFn(updateShipmentIssuePolicy);

  const { data: rows = [], isLoading } = useQuery<ShipmentIssuePolicyRow[]>({
    queryKey: ["shipment-issue-policies", tenantId],
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });

  const toggle = useMutation({
    mutationFn: async (v: { reasonCode: string; portalVisibility: boolean }) =>
      updateFn({ data: { tenantId: tenantId!, ...v } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment-issue-policies", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Shipment issues</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Carrier-reported issues (return to sender, undeliverable, damaged, delays) show on the
          Operations Workspace automatically. Choose which of them also surface on the customer portal.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Portal visibility</CardTitle>
          <CardDescription>
            When enabled, customers see a plain-language notice on their portal for shipments with this issue.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Issue</TableHead>
                <TableHead>Customer sees</TableHead>
                <TableHead className="w-40 text-right">Show on portal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No policies yet.</TableCell></TableRow>
              ) : (
                rows.map((r) => {
                  const copy = SHIPMENT_ISSUE_COPY[r.reason_code as ShipmentExceptionReasonCode] ?? SHIPMENT_ISSUE_COPY.other;
                  return (
                    <TableRow key={r.id}>
                      <TableCell>
                        <div className="font-medium capitalize">{r.reason_code.replace(/_/g, " ")}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{r.reason_code}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{copy.title}</div>
                        <div className="text-muted-foreground">{copy.body}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={r.portal_visibility}
                          onCheckedChange={(v) => toggle.mutate({ reasonCode: r.reason_code, portalVisibility: v })}
                          disabled={toggle.isPending}
                          aria-label={`Toggle portal visibility for ${r.reason_code}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}